/**
 * O José, o Desk Tutor (docs/spec/desk-tutor.md): a conta da IA que responde dúvidas sobre o RPG Desk
 * por mensagem direta e que é amiga de todo mundo. A conta tem id fixo, para o cliente reconhecer as
 * mensagens dele; os limites ficam aqui porque a administração os mostra e muda.
 */
import { z } from 'zod';

export const DESK_TUTOR_ID = 'usr_desk_tutor';
export const DESK_TUTOR_NAME = 'José - Desk Tutor';

export type DeskTutorProvider = 'anthropic' | 'deepseek';

export const deskTutorProviderOf = (model: string): DeskTutorProvider => (model.startsWith('deepseek-') ? 'deepseek' : 'anthropic');

/**
 * Os modelos que a administração escolhe para o José, na ordem da lista, com a nota "boa" no teste
 * dos modelos (tools/eval-assistente, 33 perguntas; null para o que não entrou no teste). Cada um só
 * vale com a chave do fornecedor dele no servidor.
 */
export const DESK_TUTOR_MODELS: readonly { id: string; label: string; provider: DeskTutorProvider; score: number | null }[] = [
  { id: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5', provider: 'anthropic', score: 86 },
  { id: 'claude-opus-5-5', label: 'Claude Opus 5.5', provider: 'anthropic', score: 92 },
  { id: 'claude-haiku-5-5', label: 'Claude Haiku 5.5', provider: 'anthropic', score: 71 },
  { id: 'deepseek-flash', label: 'DeepSeek Flash', provider: 'deepseek', score: 77 },
  { id: 'deepseek-v4-pro', label: 'DeepSeek V4 Pro', provider: 'deepseek', score: null },
];

/** O nome de um modelo na tela (o id, para um modelo fora da lista). */
export const deskTutorModelLabel = (model: string): string => DESK_TUTOR_MODELS.find((m) => m.id === model)?.label ?? model;

/** Os limites e a chave de ligar, que os administradores da configuração do servidor mudam. */
export const DeskTutorSettingsSchema = z.object({
  enabled: z.boolean(),
  /** perguntas respondidas a cada pessoa em 24 horas */
  perUserDaily: z.number().int().min(1).max(1000),
  /** a espera mínima entre duas perguntas da mesma pessoa, em segundos */
  minIntervalSeconds: z.number().int().min(0).max(3600),
  /** perguntas respondidas no servidor inteiro em 24 horas */
  serverDaily: z.number().int().min(1).max(100_000),
});
export type DeskTutorSettings = z.infer<typeof DeskTutorSettingsSchema>;

/** Sem nada gravado na administração. */
export const DEFAULT_DESK_TUTOR_SETTINGS: DeskTutorSettings = { enabled: true, perUserDaily: 40, minIntervalSeconds: 10, serverDaily: 500 };

/** As respostas das últimas 24 horas, para a seção da administração. */
export type DeskTutorDay = {
  /** as que a IA respondeu */
  answered: number;
  /** as que deram erro (a pessoa recebeu o "não consegui responder") */
  failed: number;
  /** quantas pessoas perguntaram */
  people: number;
  /** o gasto estimado pelos tokens, em US$; null quando o modelo não está na tabela de preços */
  costUsd: number | null;
};

/** Um modelo na seção José: se a chave do fornecedor dele está no servidor. */
export type AdminDeskTutorModel = { id: string; label: string; provider: DeskTutorProvider; score: number | null; available: boolean };

/** O que a seção José grava (`PUT /api/admin/desk-tutor`): os limites e, se veio, o modelo. */
export const DeskTutorChangeSchema = DeskTutorSettingsSchema.extend({ model: z.string().min(1).max(64).optional() });
export type DeskTutorChange = z.infer<typeof DeskTutorChangeSchema>;

/** A seção José da administração (`GET /api/admin/desk-tutor`). */
export type AdminDeskTutorInfo = {
  /** o modelo de agora: o escolhido na administração ou o padrão do servidor; null sem chave nenhuma */
  model: string | null;
  /** o padrão do servidor (RPGDESK_ASSISTANT_MODEL, ou o Sonnet) */
  defaultModel: string | null;
  models: AdminDeskTutorModel[];
  settings: DeskTutorSettings;
  updatedAt: number | null;
  updatedByName: string | null;
  /** só os administradores da configuração do servidor mudam */
  canEdit: boolean;
  day: DeskTutorDay;
};

/** O `detail` da troca dos limites (e do modelo, quando ele mudou) no registro da administração, em JSON. */
export const AdminDeskTutorDetailSchema = z.object({
  before: DeskTutorSettingsSchema,
  after: DeskTutorSettingsSchema,
  model: z.object({ before: z.string(), after: z.string() }).optional(),
});
export type AdminDeskTutorDetail = z.infer<typeof AdminDeskTutorDetailSchema>;
